<template>
  <v-autocomplete
    :value="value"
    :items="filteredIcons"
    :search-input.sync="searchInput"
    :label="label"
    :rules="rules"
    :dense="dense"
    :disabled="disabled"
    :clearable="clearable"
    :hide-details="hideDetails"
    :menu-props="{ maxHeight: 320 }"
    placeholder="Search icons"
    no-filter
    auto-select-first
    @input="$emit('input', $event)"
  >
    <template v-slot:selection="{ item }">
      <v-icon left small>{{ item }}</v-icon>
      <span class="text-truncate">{{ item }}</span>
    </template>

    <template v-slot:item="{ item }">
      <v-list-item-icon class="mr-3">
        <v-icon>{{ item }}</v-icon>
      </v-list-item-icon>
      <v-list-item-content>
        <v-list-item-title>{{ item }}</v-list-item-title>
      </v-list-item-content>
    </template>

    <template v-slot:no-data>
      <v-list-item dense>
        <v-list-item-content>
          <v-list-item-title class="text--secondary">
            No icons match that search
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>

    <template v-slot:append-item>
      <v-list-item v-if="hiddenCount > 0" dense>
        <v-list-item-content>
          <v-list-item-subtitle class="text--secondary">
            {{ hiddenCount }} more — keep typing to narrow the list
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script>
// Number of icons rendered in the menu at once. The full MDI set is ~7500
// icons, and v-autocomplete renders every item it is given, so the list is
// capped and the user narrows it by typing.
const MAX_RESULTS = 100;

// Used when the icon names cannot be read off the stylesheet (for example
// when @mdi/font is served from a CDN, which makes cssRules unreadable).
const FALLBACK_ICONS = [
  "mdi-account",
  "mdi-account-group",
  "mdi-account-multiple",
  "mdi-account-school",
  "mdi-account-tie",
  "mdi-alarm",
  "mdi-alert",
  "mdi-alert-circle",
  "mdi-alert-octagon",
  "mdi-application",
  "mdi-archive",
  "mdi-arrow-right",
  "mdi-arrow-right-circle",
  "mdi-attachment",
  "mdi-baseball",
  "mdi-basketball",
  "mdi-bell",
  "mdi-bell-ring",
  "mdi-book",
  "mdi-book-open-page-variant",
  "mdi-book-open-variant",
  "mdi-bookmark",
  "mdi-briefcase",
  "mdi-brush",
  "mdi-bullhorn",
  "mdi-bus",
  "mdi-cake-variant",
  "mdi-calendar",
  "mdi-calendar-check",
  "mdi-calendar-clock",
  "mdi-calendar-month",
  "mdi-calendar-star",
  "mdi-camera",
  "mdi-cash",
  "mdi-cash-multiple",
  "mdi-certificate",
  "mdi-chart-bar",
  "mdi-chart-line",
  "mdi-check",
  "mdi-check-circle",
  "mdi-check-decagram",
  "mdi-checkbox-marked-circle",
  "mdi-chess-knight",
  "mdi-clipboard-check",
  "mdi-clipboard-text",
  "mdi-clock",
  "mdi-clock-outline",
  "mdi-cloud",
  "mdi-coffee",
  "mdi-comment-text",
  "mdi-cricket",
  "mdi-crown",
  "mdi-cup",
  "mdi-currency-usd",
  "mdi-desk",
  "mdi-download",
  "mdi-drama-masks",
  "mdi-email",
  "mdi-email-open",
  "mdi-emoticon-happy",
  "mdi-exclamation-thick",
  "mdi-eye",
  "mdi-file",
  "mdi-file-document",
  "mdi-file-document-edit",
  "mdi-file-pdf-box",
  "mdi-filmstrip",
  "mdi-fire",
  "mdi-flag",
  "mdi-flask",
  "mdi-food",
  "mdi-food-apple",
  "mdi-football",
  "mdi-gift",
  "mdi-google-classroom",
  "mdi-guitar-acoustic",
  "mdi-hand-heart",
  "mdi-heart",
  "mdi-help-circle",
  "mdi-home",
  "mdi-home-heart",
  "mdi-human-male-board",
  "mdi-image",
  "mdi-image-multiple",
  "mdi-information",
  "mdi-key",
  "mdi-laptop",
  "mdi-leaf",
  "mdi-library",
  "mdi-lightbulb",
  "mdi-link",
  "mdi-map-marker",
  "mdi-medal",
  "mdi-medical-bag",
  "mdi-microphone",
  "mdi-microscope",
  "mdi-music",
  "mdi-music-note",
  "mdi-newspaper-variant",
  "mdi-note-text",
  "mdi-numeric",
  "mdi-palette",
  "mdi-paperclip",
  "mdi-pencil",
  "mdi-phone",
  "mdi-piano",
  "mdi-pin",
  "mdi-play-circle",
  "mdi-podium",
  "mdi-printer",
  "mdi-pulse",
  "mdi-rocket-launch",
  "mdi-ruler",
  "mdi-run",
  "mdi-school",
  "mdi-shield-check",
  "mdi-shoe-print",
  "mdi-soccer",
  "mdi-star",
  "mdi-star-circle",
  "mdi-swim",
  "mdi-tag",
  "mdi-teach",
  "mdi-tennis",
  "mdi-test-tube",
  "mdi-thumb-up",
  "mdi-ticket",
  "mdi-ticket-confirmation",
  "mdi-timer",
  "mdi-tools",
  "mdi-trophy",
  "mdi-trophy-award",
  "mdi-truck",
  "mdi-umbrella",
  "mdi-video",
  "mdi-volleyball",
  "mdi-wallet",
  "mdi-water",
  "mdi-weather-sunny",
  "mdi-web",
  "mdi-whistle",
];

// Resolved once per page load and shared by every instance of the picker.
let cachedIconNames = null;

// The MDI font ships one `.mdi-<name>::before` rule per icon, so the installed
// icon set can be read straight off the loaded stylesheets. That keeps the
// picker in sync with whatever version of @mdi/font the app has, at no cost to
// the bundle size.
function readIconNamesFromStylesheets() {
  if (typeof document === "undefined") return [];

  const names = new Set();
  const ignored = new Set(["mdi-set", "mdi-blank"]);

  for (const sheet of Array.from(document.styleSheets)) {
    let rules;

    try {
      rules = sheet.cssRules;
    } catch (error) {
      // Cross-origin stylesheet, its rules cannot be read.
      continue;
    }

    if (!rules) continue;

    for (const rule of Array.from(rules)) {
      const selector = rule.selectorText;
      if (!selector || selector.indexOf(".mdi-") === -1) continue;

      const matches = selector.match(/\.(mdi-[a-z0-9-]+)::?before/g);
      if (!matches) continue;

      matches.forEach((match) => {
        const name = match.slice(1).split(":")[0];
        if (!ignored.has(name)) names.add(name);
      });
    }
  }

  return Array.from(names).sort();
}

function loadIconNames() {
  if (cachedIconNames) return cachedIconNames;

  const fromStylesheets = readIconNamesFromStylesheets();

  cachedIconNames =
    fromStylesheets.length > 50
      ? fromStylesheets
      : FALLBACK_ICONS.slice().sort();

  return cachedIconNames;
}

export default {
  name: "IconPicker",

  props: {
    value: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: "Icon",
    },
    rules: {
      type: Array,
      default: () => [],
    },
    dense: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    hideDetails: {
      type: [Boolean, String],
      default: false,
    },
  },

  data: () => ({
    searchInput: "",
    allIcons: [],
  }),

  computed: {
    matchingIcons() {
      const query = (this.searchInput || "")
        .trim()
        .toLowerCase()
        .replace(/^mdi-/, "")
        .replace(/\s+/g, "-");

      if (query === "") return this.allIcons;

      const startsWith = [];
      const contains = [];

      for (const name of this.allIcons) {
        const bareName = name.slice(4); // drop the "mdi-" prefix
        const index = bareName.indexOf(query);

        if (index === 0) {
          startsWith.push(name);
        } else if (index > 0) {
          contains.push(name);
        }
      }

      return startsWith.concat(contains);
    },

    filteredIcons() {
      const visible = this.matchingIcons.slice(0, MAX_RESULTS);

      // v-autocomplete can only render a selection that exists in its items,
      // so the current value is always kept in the list.
      if (this.value && !visible.includes(this.value)) {
        return [this.value, ...visible];
      }

      return visible;
    },

    hiddenCount() {
      return Math.max(this.matchingIcons.length - MAX_RESULTS, 0);
    },
  },

  created() {
    this.allIcons = loadIconNames();
  },
};
</script>
